import { useQuery, UseQueryResult } from "react-query";

import type { CubeLoadQuery } from "./cubeQueryTypes";
import { loadCustomersNoRegionWithPostal } from "./customersViewFactsData";
import { loadUnassignedProCurrentYear } from "./unassignedProFactsData";
import { loadCustomersWithProductAvailabilityByRegion } from "./customersWithProductAvailabilityByRegionData";
import { loadProContractorsByMonthRegion } from "./proContractorsData";

const queryKeyPro = "crmHomeProContractors" as const;
const queryKeyAdminAvail = "crmHomeAdminCustomersWithAvailabilityByRegion" as const;
const queryKeyUnassigned = "crmHomeUnassignedProCalendarYear" as const;
const queryKeyNoRegionPostal = "crmHomeCustomersNoRegionPostal" as const;
const queryKeyMyAvail = "crmHomeMyRegionAvailability" as const;

type Params = {
  cubeLoad: (query: CubeLoadQuery) => Promise<unknown>;
  canQueryCubeBase: boolean;
  isDirectorAnalytics: boolean;
  regionTrimmed: string;
  regionLoaded: boolean;
  canQueryCubeWithoutRegionSelect: boolean;
};

export const useCrmHomeCubeQueries = (p: Params) => {
  const hasRegionScope =
    p.isDirectorAnalytics ||
    (p.regionLoaded && (p.regionTrimmed.length > 0 || p.canQueryCubeWithoutRegionSelect));
  const canQueryPro = p.canQueryCubeBase && hasRegionScope;
  const canQueryAdminAvail = p.canQueryCubeBase && p.isDirectorAnalytics;
  const canQueryUnassigned = p.canQueryCubeBase && p.isDirectorAnalytics;
  const canQueryNoPostal = p.canQueryCubeBase && p.isDirectorAnalytics;
  const canQueryMyAvail =
    p.canQueryCubeBase && !p.isDirectorAnalytics && p.regionLoaded && p.regionTrimmed.length > 0;

  const proQuery = useQuery(
    [queryKeyPro, p.isDirectorAnalytics ? "all" : p.regionTrimmed],
    () =>
      loadProContractorsByMonthRegion({
        cubeLoad: p.cubeLoad,
        regionFilter: p.isDirectorAnalytics ? null : p.regionTrimmed,
      }),
    {
      staleTime: 120_000,
      retry: 1,
      enabled: canQueryPro,
    },
  );

  const adminAvailQuery = useQuery(
    [queryKeyAdminAvail],
    () =>
      loadCustomersWithProductAvailabilityByRegion({
        cubeLoad: p.cubeLoad,
      }),
    {
      staleTime: 120_000,
      retry: 1,
      enabled: canQueryAdminAvail,
    },
  );

  const calendarYear = new Date().getFullYear();

  const unassignedQuery = useQuery(
    [queryKeyUnassigned, calendarYear],
    () => loadUnassignedProCurrentYear({ cubeLoad: p.cubeLoad }),
    {
      staleTime: 120_000,
      retry: 1,
      enabled: canQueryUnassigned,
    },
  );

  const noRegionPostalQuery = useQuery(
    [queryKeyNoRegionPostal],
    () => loadCustomersNoRegionWithPostal({ cubeLoad: p.cubeLoad }),
    {
      staleTime: 120_000,
      retry: 1,
      enabled: canQueryNoPostal,
    },
  );

  const myAvailQuery = useQuery(
    [queryKeyMyAvail, p.regionTrimmed],
    () =>
      loadCustomersWithProductAvailabilityByRegion({
        cubeLoad: p.cubeLoad,
        regionEqual: p.regionTrimmed,
      }),
    {
      staleTime: 120_000,
      retry: 1,
      enabled: canQueryMyAvail,
    },
  );

  return {
    hasRegionScope,
    canQueryMyAvail,
    anyInitialLoading:
      (canQueryPro && proQuery.isLoading) ||
      (canQueryAdminAvail && adminAvailQuery.isLoading) ||
      (canQueryUnassigned && unassignedQuery.isLoading) ||
      (canQueryNoPostal && noRegionPostalQuery.isLoading) ||
      (canQueryMyAvail && myAvailQuery.isLoading),
    proQuery,
    adminAvailQuery,
    unassignedQuery,
    noRegionPostalQuery,
    myAvailQuery,
  };
};

export const showWhenRowsOrError = <T>(query: UseQueryResult<T[]>): boolean =>
  query.isError || (query.isSuccess && (query.data?.length ?? 0) > 0);
